import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        // Suppress the global 5xx "Something went wrong on our end" toast
        // for this boot-time auth check. The catch block below already
        // treats any failure as "not logged in" — there's no UX value in
        // alarming an anonymous visitor with an error toast on first
        // landing while the backend is still cold-starting. Login,
        // signup, and feature-specific calls still surface their errors.
        _suppressToast: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      {
        withCredentials: true,
        // LoginPage handles all login error messaging itself (it
        // distinguishes 401 "wrong creds" from 5xx "server warming up").
        // Suppressing the global 5xx toast here prevents a confusing
        // double-toast on cold-start.
        _suppressToast: true,
      }
    );
    // Defense-in-depth: persist the session token to localStorage so the
    // Bearer fallback in `lib/api.js` works even if Cloudflare/proxy strips
    // the Set-Cookie response (we've seen this happen in some prod setups).
    if (response.data?.token) {
      try { localStorage.setItem("wokelo_session_token", response.data.token); } catch (_) { /* */ }
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, referralCode) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { email, password, name, referral_code: referralCode },
      { withCredentials: true, _suppressToast: true }
    );
    // New two-stage signup: backend returns {verification_sent:true} for the
    // standard path and only returns the full user payload for the very-first
    // bootstrap admin. Don't setUser unless we actually got a real session.
    if (response.data && !response.data.verification_sent && response.data.user_id) {
      setUser(response.data);
    }
    return response.data;
  };

  const verifyEmail = async (token) => {
    const response = await axios.post(
      `${API}/auth/verify-email`,
      { token },
      { withCredentials: true }
    );
    if (response.data?.token) {
      try { localStorage.setItem("wokelo_session_token", response.data.token); } catch (_) { /* */ }
    }
    setUser(response.data);
    return response.data;
  };

  const googleAuth = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/google`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore errors
    }
    // Clear localStorage-backed session token (Bearer fallback used when
    // Cloudflare/browser strips Set-Cookie). Without this, a logged-out user
    // would still send a stale Bearer token on next request.
    try {
      const { clearStoredToken } = await import("../lib/api");
      clearStoredToken();
    } catch (_) { /* lib not loaded — nothing to clear */ }
    setUser(null);
  };

  const updatePreferences = async (preferences) => {
    await axios.put(`${API}/auth/preferences`, preferences, {
      withCredentials: true,
    });
    setUser((prev) => ({
      ...prev,
      preferences: { ...prev?.preferences, ...preferences },
    }));
  };

  const value = {
    user,
    loading,
    login,
    register,
    verifyEmail,
    googleAuth,
    logout,
    updatePreferences,
    checkAuth,
    refreshUser: checkAuth,
    isAdmin: user?.role === "admin",
    impersonation: user?._impersonation || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
